import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flame, Footprints, Moon, Droplets, TrendingUp, Play, ChevronRight, 
  Award, Heart, Zap, Target, Plus, MoreHorizontal, Trophy, 
  Timer, Dumbbell, Activity
} from 'lucide-react';

export default function FitnessPreview({ customization }) {
  const theme = customization?.theme || { primary: '#6366f1', secondary: '#8b5cf6', gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' };
  const companyName = customization?.companyName || 'FitPro';
  
  const [activeTab, setActiveTab] = useState('today');
  const [steps, setSteps] = useState(8247);
  const [calories, setCalories] = useState(1680);
  const [heartRate, setHeartRate] = useState(72);
  const [showCelebration, setShowCelebration] = useState(false);

  // Simulate live data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setSteps(prev => {
        const newSteps = prev + Math.floor(Math.random() * 8);
        if (newSteps >= 10000 && prev < 10000) {
          setShowCelebration(true);
          setTimeout(() => setShowCelebration(false), 3000);
        }
        return newSteps;
      });
      setCalories(prev => prev + Math.floor(Math.random() * 3));
      setHeartRate(prev => 70 + Math.floor(Math.random() * 10));
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const weekData = [45, 72, 88, 55, 95, 78, 62];
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const today = 4; // Friday

  const workouts = [
    { name: 'Morning HIIT', duration: '28 min', calories: 380, icon: '🔥', intensity: 'High', completed: true },
    { name: 'Strength Training', duration: '45 min', calories: 290, icon: '💪', intensity: 'Medium', scheduled: '4:00 PM' },
  ];

  const progressPercent = Math.min((steps / 10000) * 100, 100);

  return (
    <div style={{ 
      height: '100%', 
      background: `linear-gradient(180deg, #0f172a 0%, ${theme.primary}10 100%)`,
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      color: 'white',
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      {/* Celebration overlay */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1], rotate: [0, 10, -10, 0] }}
              style={{ fontSize: '64px', marginBottom: '16px' }}
            >
              🎉
            </motion.div>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 8px' }}>Goal Achieved!</h2>
            <p style={{ fontSize: '14px', color: '#94a3b8' }}>You've reached 10,000 steps!</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status Bar */}
      <div style={{ 
        padding: '8px 16px', 
        display: 'flex', 
        justifyContent: 'space-between',
        fontSize: '11px'
      }}>
        <span style={{ fontWeight: '500' }}>9:41</span>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <span>📶</span>
          <span>🔋</span>
        </div>
      </div>

      {/* Header */}
      <div style={{ padding: '8px 16px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>Good morning</p>
            <h1 style={{ margin: '2px 0 0 0', fontSize: '20px', fontWeight: '700' }}>
              {companyName} User <motion.span animate={{ rotate: [0, 20, 0] }} transition={{ duration: 0.5, delay: 1 }}>👋</motion.span>
            </h1>
          </div>
          <motion.div 
            whileTap={{ scale: 0.95 }}
            style={{
              width: '40px', height: '40px', borderRadius: '50%',
              background: theme.gradient,
              border: '3px solid rgba(255,255,255,0.2)',
              boxShadow: `0 4px 15px ${theme.primary}40`
            }} 
          />
        </div>
      </div>

      {/* Main Ring Card */}
      <div style={{ padding: '0 16px', marginBottom: '16px' }}>
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={{
            background: theme.gradient,
            borderRadius: '24px',
            padding: '20px',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 10px 40px ${theme.primary}40`
          }}
        >
          {/* Decorative circles */}
          <div style={{
            position: 'absolute', top: '-30px', right: '-30px',
            width: '120px', height: '120px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)'
          }} />
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Progress Ring */}
            <div style={{ position: 'relative', width: '100px', height: '100px' }}>
              <svg width="100" height="100" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="8" />
                <motion.circle 
                  cx="50" cy="50" r="42" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 42}`}
                  initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                  animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - progressPercent / 100) }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </svg>
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center'
              }}>
                <Footprints size={18} />
                <motion.span 
                  key={steps}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  style={{ fontSize: '18px', fontWeight: '700', marginTop: '2px' }}
                >
                  {steps.toLocaleString()}
                </motion.span>
                <span style={{ fontSize: '9px', opacity: 0.8 }}>/ 10,000</span>
              </div>
            </div>
            
            {/* Stats */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { icon: Flame, value: calories.toLocaleString(), label: 'kcal' },
                { icon: Heart, value: heartRate, label: 'bpm' },
                { icon: Timer, value: '1.8h', label: 'active' },
              ].map((stat, i) => (
                <motion.div 
                  key={stat.label}
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '8px',
                    background: 'rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <stat.icon size={14} />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{stat.value}</div>
                    <div style={{ fontSize: '9px', opacity: 0.7 }}>{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* AI Coach */}
      <div style={{ padding: '0 16px', marginBottom: '12px' }}>
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          whileTap={{ scale: 0.98 }}
          style={{
            background: `linear-gradient(90deg, ${theme.primary}20, ${theme.primary}08)`,
            borderRadius: '14px',
            padding: '12px 14px',
            border: `1px solid ${theme.primary}40`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer'
          }}
        >
          <motion.div 
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              width: '40px', height: '40px', borderRadius: '12px',
              background: theme.gradient,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 15px ${theme.primary}40`
            }}
          >
            <Zap size={20} color="white" />
          </motion.div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: theme.primary }}>{companyName} AI Coach</p>
            <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#94a3b8' }}>
              {steps < 10000 
                ? `${(10000 - steps).toLocaleString()} more steps to hit your goal! 💪`
                : 'Amazing! You crushed your goal today! 🎉'}
            </p>
          </div>
          <ChevronRight size={18} color={theme.primary} />
        </motion.div>
      </div>

      {/* Weekly Progress */}
      <div style={{ padding: '0 16px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>Weekly Progress</h3>
          <span style={{ fontSize: '10px', color: theme.primary }}>View details →</span>
        </div>
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '14px',
          padding: '14px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', height: '70px' }}>
            {weekData.map((value, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1 }}>
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${value}%` }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  whileHover={{ scale: 1.1 }}
                  style={{
                    width: '24px',
                    background: i === today 
                      ? theme.gradient
                      : i < today ? `${theme.primary}60` : `${theme.primary}25`,
                    borderRadius: '6px',
                    minHeight: '8px',
                    boxShadow: i === today ? `0 4px 12px ${theme.primary}60` : 'none',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ 
                  fontSize: '10px', 
                  fontWeight: i === today ? '600' : '400',
                  color: i === today ? 'white' : '#64748b' 
                }}>{days[i]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Today's Workouts */}
      <div style={{ padding: '0 16px', flex: 1, overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>Today's Workouts</h3>
          <motion.button 
            whileTap={{ scale: 0.95 }}
            style={{
              background: `${theme.primary}30`,
              border: 'none',
              borderRadius: '8px',
              padding: '6px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: theme.primary,
              fontSize: '10px',
              cursor: 'pointer'
            }}
          >
            <Plus size={12} /> Add
          </motion.button>
        </div>
        {workouts.map((workout, i) => (
          <motion.div
            key={workout.name}
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.5 + i * 0.1 }}
            whileTap={{ scale: 0.98 }}
            whileHover={{ x: 4 }}
            style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '14px',
              padding: '14px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              border: workout.completed ? `1px solid ${theme.primary}40` : '1px solid transparent',
              cursor: 'pointer'
            }}
          >
            <div style={{
              width: '44px', height: '44px', borderRadius: '12px',
              background: workout.completed 
                ? theme.gradient 
                : `${theme.primary}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '22px'
            }}>
              {workout.completed ? '✓' : workout.icon}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: '500' }}>{workout.name}</p>
                {workout.completed && (
                  <span style={{ fontSize: '8px', padding: '2px 6px', background: `${theme.primary}30`, color: theme.primary, borderRadius: '4px' }}>
                    Done
                  </span>
                )}
              </div>
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                {workout.duration} • {workout.calories} kcal • {workout.intensity}
              </p>
              {workout.scheduled && (
                <p style={{ margin: '2px 0 0', fontSize: '10px', color: theme.primary }}>
                  Scheduled: {workout.scheduled}
                </p>
              )}
            </div>
            {!workout.completed && (
              <motion.button
                whileTap={{ scale: 0.9 }}
                style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: theme.gradient,
                  border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: `0 4px 12px ${theme.primary}60`
                }}
              >
                <Play size={14} color="white" fill="white" />
              </motion.button>
            )}
          </motion.div>
        ))}
      </div>

      {/* Bottom Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        padding: '12px 0 8px',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(0,0,0,0.3)'
      }}>
        {[
          { icon: '🏠', label: 'Home', active: true },
          { icon: '📊', label: 'Stats' },
          { icon: '➕', label: 'Add', special: true },
          { icon: '🏆', label: 'Goals' },
          { icon: '👤', label: 'Profile' }
        ].map((item, i) => (
          <motion.div 
            key={item.label}
            whileTap={{ scale: 0.9 }}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              opacity: item.active ? 1 : 0.5,
              cursor: 'pointer'
            }}
          >
            {item.special ? (
              <div style={{
                width: '44px', height: '44px', borderRadius: '50%',
                background: theme.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginTop: '-20px',
                boxShadow: `0 4px 15px ${theme.primary}60`
              }}>
                <span style={{ fontSize: '20px' }}>{item.icon}</span>
              </div>
            ) : (
              <span style={{ fontSize: '20px' }}>{item.icon}</span>
            )}
            <span style={{ fontSize: '9px', marginTop: '4px', color: item.active ? 'white' : '#64748b' }}>
              {item.label}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
